import { BaseService } from '@/@admin/services/base.service';
import type {
    CreateFeatureFlagRequest,
    CreateOverrideRequest,
    FeatureFlagAccessResponse,
    FeatureFlagGroupsResponse,
    FeatureFlagListResponse,
    FeatureFlagResponse,
    UpdateFeatureFlagRequest,
} from '@/@feature-flags/types/api';
import { withLocalFlagOverrides } from '@/@feature-flags/local-overrides';
import { FEATURE_FLAGS_DEFAULTS, FeatureFlagEndpoints } from '@/@feature-flags/types/constants';
import { tracking } from '@/lib/features/tracking';
import { portalApi } from '@/lib/http/axios';

function encodeFlagKey(key: string): string {
    return encodeURIComponent(key);
}

export class FeatureFlagService extends BaseService {
    constructor() {
        super(portalApi);
    }

    async checkAccess(keys: string[]): Promise<FeatureFlagAccessResponse> {
        try {
            const { data } = await this.axiosInstance.get<FeatureFlagAccessResponse>(
                FeatureFlagEndpoints.ACCESS,
                { params: { keys: keys.join(',') } },
            );
            return { ...data, flags: withLocalFlagOverrides(data.flags ?? {}, keys) };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to check feature flag access: ${message}`),
                'FeatureFlagService.checkAccess',
            );
            return { flags: withLocalFlagOverrides({}, keys) };
        }
    }

    /**
     * Uncached access check for QA tooling: hits the API directly and propagates errors.
     */
    async fetchAccessLive(keys: string[]): Promise<FeatureFlagAccessResponse> {
        if (keys.length === 0) {
            return { flags: {} };
        }

        try {
            const { data } = await this.axiosInstance.get<FeatureFlagAccessResponse>(
                FeatureFlagEndpoints.ACCESS,
                { params: { keys: keys.join(',') } },
            );
            return data;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed live feature flag access check: ${message}`),
                'FeatureFlagService.fetchAccessLive',
            );
            throw error;
        }
    }

    async list(page = 1, limit = 25): Promise<FeatureFlagListResponse> {
        try {
            const { data } = await this.axiosInstance.get<FeatureFlagListResponse>(
                FeatureFlagEndpoints.BASE,
                { params: { page, limit } },
            );
            return data;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to list feature flags: ${message}`),
                'FeatureFlagService.list',
            );
            throw error;
        }
    }

    /**
     * Every flag key in this environment. The admin list is paged, and the
     * catalog has to know whether a key exists even when it is not on the
     * current page.
     */
    async listAllKeys(): Promise<string[]> {
        const keys: string[] = [];
        const limit = FEATURE_FLAGS_DEFAULTS.MAX_LIMIT;
        let page = 1;

        for (;;) {
            const data = await this.list(page, limit);
            for (const flag of data.data) keys.push(flag.key);
            const pageLimit = data.limit > 0 ? data.limit : limit;
            const reachedEnd =
                data.data.length === 0 || keys.length >= data.total || data.data.length < pageLimit;
            if (reachedEnd) return keys;
            page += 1;
        }
    }

    async getByKey(key: string): Promise<FeatureFlagResponse> {
        try {
            const { data } = await this.axiosInstance.get<FeatureFlagResponse>(
                `${FeatureFlagEndpoints.BASE}/${encodeFlagKey(key)}`,
            );
            return data;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get feature flag "${key}": ${message}`),
                'FeatureFlagService.getByKey',
            );
            throw error;
        }
    }

    async create(payload: CreateFeatureFlagRequest): Promise<FeatureFlagResponse> {
        try {
            const { data } = await this.axiosInstance.post<FeatureFlagResponse>(
                FeatureFlagEndpoints.BASE,
                payload,
            );
            this.clearCache();
            return data;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to create feature flag: ${message}`),
                'FeatureFlagService.create',
            );
            throw error;
        }
    }

    async update(key: string, payload: UpdateFeatureFlagRequest): Promise<FeatureFlagResponse> {
        try {
            const { data } = await this.axiosInstance.patch<FeatureFlagResponse>(
                `${FeatureFlagEndpoints.BASE}/${encodeFlagKey(key)}`,
                payload,
            );
            this.clearCache();
            return data;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to update feature flag "${key}": ${message}`),
                'FeatureFlagService.update',
            );
            throw error;
        }
    }

    async delete(key: string): Promise<void> {
        try {
            await this.axiosInstance.delete(`${FeatureFlagEndpoints.BASE}/${encodeFlagKey(key)}`);
            this.clearCache();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to delete feature flag "${key}": ${message}`),
                'FeatureFlagService.delete',
            );
            throw error;
        }
    }

    async addOverride(key: string, payload: CreateOverrideRequest): Promise<void> {
        try {
            await this.axiosInstance.post(
                `${FeatureFlagEndpoints.BASE}/${encodeFlagKey(key)}/overrides`,
                payload,
            );
            this.clearCache();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to add override for "${key}": ${message}`),
                'FeatureFlagService.addOverride',
            );
            throw error;
        }
    }

    async removeOverride(key: string, overrideId: number): Promise<void> {
        try {
            await this.axiosInstance.delete(
                `${FeatureFlagEndpoints.BASE}/${encodeFlagKey(key)}/overrides/${overrideId}`,
            );
            this.clearCache();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to remove override ${overrideId} for "${key}": ${message}`),
                'FeatureFlagService.removeOverride',
            );
            throw error;
        }
    }

    async listGroups(): Promise<string[]> {
        try {
            const { data } = await this.axiosInstance.get<FeatureFlagGroupsResponse>(
                FeatureFlagEndpoints.GROUPS,
            );
            return data.groups;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to list groups: ${message}`),
                'FeatureFlagService.listGroups',
            );
            throw error;
        }
    }

    async removeAllOverrides(key: string): Promise<void> {
        try {
            await this.axiosInstance.delete(
                `${FeatureFlagEndpoints.BASE}/${encodeFlagKey(key)}/overrides`,
            );
            this.clearCache();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to remove all overrides for "${key}": ${message}`),
                'FeatureFlagService.removeAllOverrides',
            );
            throw error;
        }
    }
}

export const featureFlagService = new FeatureFlagService();
